import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { getPhase } from "../data/constants";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, FileText, BarChart3, MessageSquare, Download, Loader2, Send } from "lucide-react";
import { fmtD } from "../lib/date";

const TABS = [
  { key: "log", icon: FileText, zh: "施工日志", en: "Construction Log" },
  { key: "report", icon: BarChart3, zh: "运营报表", en: "Ops Report" },
  { key: "chat", icon: MessageSquare, zh: "AI 助理", en: "AI Assistant" },
];

function download(name, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
}

export default function AIAssistant() {
  const { lang, api, visibleProjects, user } = useApp();
  const [tab, setTab] = useState("log");
  const [projId, setProjId] = useState("");
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [logs, setLogs] = useState([]);
  const chatEnd = useRef();
  const sessionId = useRef(`chat-${user.username}-${Date.now()}`);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const loadLogs = async (pid) => {
    try { const { data } = await api.get(`/logs${pid ? `?projectId=${pid}` : ""}`); setLogs(data); }
    catch { setLogs([]); }
  };
  useEffect(() => { if (tab === "log" && projId) loadLogs(projId); }, [projId, tab]); // eslint-disable-line

  const genLog = async () => {
    if (!projId) return;
    setLoading(true); setContent("");
    try { const { data } = await api.post("/ai/log", { projectId: projId, lang }); setContent(data.content); await loadLogs(projId); }
    catch (e) { setContent((lang === "zh" ? "生成失败: " : "Failed: ") + (e?.detailText || e.message)); }
    finally { setLoading(false); }
  };
  const genReport = async () => {
    setLoading(true); setContent("");
    try { const { data } = await api.post("/ai/report", { lang }); setContent(data.content); }
    catch (e) { setContent((lang === "zh" ? "生成失败: " : "Failed: ") + (e?.detailText || e.message)); }
    finally { setLoading(false); }
  };
  const sendChat = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim(); setInput(""); setMessages((m) => [...m, { role: "user", text: q }]); setLoading(true);
    try { const { data } = await api.post("/ai/chat", { sessionId: sessionId.current, message: q, lang }); setMessages((m) => [...m, { role: "assistant", text: data.reply }]); }
    catch (e) { setMessages((m) => [...m, { role: "assistant", text: (lang === "zh" ? "出错: " : "Error: ") + (e?.detailText || e.message) }]); }
    finally { setLoading(false); }
  };

  const selProj = visibleProjects.find((p) => p.id === projId);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-red-50 flex items-center justify-center"><Sparkles className="h-5 w-5 text-brand-500" /></div>
        <h1 className="text-2xl font-bold text-slate-900">{lang === "zh" ? "AI 智能助手" : "AI Assistant"}</h1>
        <span className="text-xs text-slate-400">GPT · {lang === "zh" ? "可访问平台全部数据" : "with full platform data"}</span>
      </div>

      <div className="flex gap-2">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => { setTab(tb.key); setContent(""); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === tb.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:text-slate-900"}`}>
            <tb.icon className="h-4 w-4" /> {lang === "zh" ? tb.zh : tb.en}
          </button>
        ))}
      </div>

      {tab === "log" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={projId} onValueChange={setProjId}>
              <SelectTrigger className="w-80"><SelectValue placeholder={lang === "zh" ? "选择项目生成施工日志" : "Select project"} /></SelectTrigger>
              <SelectContent>
                {visibleProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={genLog} disabled={!projId || loading} className="bg-brand-500 hover:bg-brand-600 text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}{lang === "zh" ? "生成施工日志" : "Generate Log"}
            </Button>
          </div>
          <Output content={content} loading={loading} lang={lang} onDownload={() => download(`施工日志-${selProj?.address || "log"}.txt`, content)} />
          {projId && logs.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-slate-700 mb-2">{lang === "zh" ? "历史施工日志归档" : "Archived Logs"} <span className="text-slate-400 font-normal">({logs.length})</span></p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {logs.map((lg) => (
                  <button key={lg.id} onClick={() => setContent(lg.content)}
                    className="w-full text-left flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2 hover:border-brand-300 hover:bg-brand-50/40 transition-colors">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-3.5 w-3.5 text-slate-400" />{fmtD(lg.date)} · {lg.phase}</span>
                    <span className="text-[11px] text-slate-400">{lg.createdBy}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "report" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={genReport} disabled={loading} className="bg-brand-500 hover:bg-brand-600 text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}{lang === "zh" ? "生成公司运营报表" : "Generate Ops Report"}
            </Button>
            {(user.role === "admin" || user.role === "ceo") && (
              <Button variant="outline" disabled={loading} onClick={async () => {
                setLoading(true);
                try { const { data } = await api.post("/reports/weekly/run", { lang }); setContent(data.content); }
                catch (e) { setContent((lang === "zh" ? "推送失败: " : "Failed: ") + (e?.detailText || e.message)); }
                finally { setLoading(false); }
              }}>{lang === "zh" ? "生成并推送周报（邮件）" : "Generate & Push Weekly"}</Button>
            )}
          </div>
          <p className="text-[11px] text-slate-400">{lang === "zh" ? "系统每周自动生成运营报表并推送给设定的收件人（需配置邮件服务）。" : "Weekly report is auto-generated & pushed to configured recipients."}</p>
          <Output content={content} loading={loading} lang={lang} onDownload={() => download("运营报表.txt", content)} />
        </div>
      )}

      {tab === "chat" && (
        <div className="bg-white rounded-xl border border-slate-200 flex flex-col" style={{ height: "60vh" }}>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {messages.length === 0 && <p className="text-slate-400 text-sm text-center py-10">{lang === "zh" ? "向 AI 询问项目进度、风险、收款等任何问题，例如：\"哪些项目严重超期？\" \"各小组收款情况如何？\"" : "Ask about progress, risks, collections..."}</p>}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bubble-mine" : "bg-slate-100 text-slate-800"}`}>{m.text}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div></div>}
            <div ref={chatEnd} />
          </div>
          <div className="border-t border-slate-100 p-3 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
              placeholder={lang === "zh" ? "输入问题..." : "Type a question..."} className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-brand-400" />
            <Button onClick={sendChat} disabled={loading} className="bg-brand-500 hover:bg-brand-600 text-white"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Output({ content, loading, lang, onDownload }) {
  if (loading && !content) return <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> {lang === "zh" ? "AI 正在生成..." : "Generating..."}</div>;
  if (!content) return null;
  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button variant="outline" size="sm" onClick={onDownload}><Download className="h-4 w-4 mr-1" /> {lang === "zh" ? "导出文件" : "Export"}</Button>
      </div>
      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed max-h-[50vh] overflow-y-auto">{content}</div>
    </div>
  );
}
